import React, { SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { RootState } from 'StoreModel';
import styled from 'styled-components';
import { CoverComponent } from '../../layout/common';
import {
  ApplicationDefinition,
  FormQuestionAnswer,
  ApplicationEntry,
  NewProject,
  Community,
  FormQuestion,
} from 'redux/types/account';
import NewForm from '../forms/view-form';
import { AcceptButton, DenyButton, PrimaryLabel, SubmitButton } from '../styled-components/common';
import { useHistory } from 'react-router-dom';
import ProjectForm from '../project/new-project-form';
import { Flag, ApplicationStatus } from '../../redux/types/enums';
import { getStatusLiteral } from '../../util/application-manager';
import { toast } from '../../components/common/toast';
import moment from 'moment';
import { getFirstMissingAnswer, isMissingAnswers, replaceLastLink, uploadSelectedFiles } from 'util/utils';
import { clearSelectedFile } from 'redux/actions/file-upload';
import { MAX_FILE_SIZE } from 'redux/types/default-types-values';
import { StyledLogo } from 'components/community/sidebar';
import { fetchMyStuffAsync } from 'redux/actions/account';
import InnerHtmlWrapper from 'components/editor/InnerHtmlWrapper';
import useTrackingScripts from 'hooks/useTrackingScripts';
import { overrideTranslation } from 'services/literals/overrides';

const ApplicationContainer = styled.div`
  flex: 1;
  max-width: 45em;
  display: flex;
  flex-direction: column;
  gap: 1em;

  @media (max-width: ${({ theme }) => theme.sizes.mobileWidth}) {
    width: 100%;
    align-self: center;
    padding: 0em 1em;
  }
`;

const Container = styled.form`
  display: flex;
  flex-direction: row;
  width: 100%;
  gap: 10em;
  justify-content: center;
  @media (max-width: ${({ theme }) => theme.sizes.mobileWidth}) {
    flex-direction: column;
    gap: 0em;
  }
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;
  max-width: 15em;
  height: 100%;
  position: sticky;
  top: 0;

  @media (max-width: ${({ theme }) => theme.sizes.mobileWidth}) {
    max-width: 45em;
    align-self: center;
    padding: 0.5em;
    padding-bottom: 2em;
  }
`;

const Row = styled.div`
  display: flex;
`;

const mapStateToProps = (state: RootState) => ({
  literals: state.literals,
  bearer: state.account.session.session.bearer,
  applications: state.account.applications,
  selectedFiles: state.selectedFiles,
  account: state.account.details.user,
  isFetchingAccount: state.loading.fetchMyStuffFlag,
});

const mapDispatchToProps = {
  clearSelectedFiles: clearSelectedFile,
  fetchMyStuff: fetchMyStuffAsync.request,
};

type dispatchType = typeof mapDispatchToProps;

export type SelectedFile = {
  question: FormQuestion;
  answer: FormQuestionAnswer[];
  file: File;
  cb: (file: File) => Promise<string>;
};

interface Props extends ReturnType<typeof mapStateToProps>, dispatchType {
  applicationDefinition: ApplicationDefinition;
  community: Community;
  defaultApplication?: ApplicationEntry;
  saveApplicationEntry: (applicationEntry: ApplicationEntry) => void;
  isSavingApplicationEntry: Flag;
  submitApplication?: (applicationEntry: ApplicationEntry) => void;
  applications: ApplicationEntry[];
}

const NewProjectApplication: React.FC<Props> = props => {
  const {
    applicationDefinition,
    saveApplicationEntry,
    defaultApplication,
    literals,
    community,
    isSavingApplicationEntry,
    submitApplication,
    applications,
    selectedFiles,
    bearer,
    clearSelectedFiles,
    account,
    fetchMyStuff,
    isFetchingAccount,
  } = props;
  const [formAnswers, setFormAnswers] = useState<FormQuestionAnswer[]>(defaultApplication?.formAnswers || []);
  const [projectAnswers, setProjectFormAnswers] = useState<FormQuestionAnswer[]>(
    defaultApplication?.projectAnswers || [],
  );
  const [newProject, setNewProject] = useState<NewProject | undefined>(undefined);
  const [submitFlag, setSubmitFlag] = useState<boolean>(false);

  const [isFormSubmitted, setIsFormSubmitted] = useState<boolean>(false);

  const [isFormSaving, setIsFormSaving] = useState<boolean>(false);

  const [isFormSubmitting, setIsFormSubmitting] = useState<boolean>(false);

  const [applicationId, setApplicationId] = useState<number>(defaultApplication?.id || 0);

  const [isFilesUploading, setIsFilesUploading] = useState<boolean>(false);

  const [projectId, setProjectId] = useState<number | null>(defaultApplication?.existingProjectId ?? null);

  const [trackingScripts, setTrackingScripts] = useState<string[]>([]);

  useEffect(() => {
    if (applicationDefinition?.trackUserAccess) {
      setTrackingScripts([...trackingScripts, applicationDefinition.trackUserAccess]);
    }
  }, [applicationDefinition]);

  useTrackingScripts(trackingScripts);

  const history = useHistory();
  const requiredFields: (keyof NewProject)[] = [
    'name',
    'description',
    'categoryId',
    'countryId',
    'city',
    'developmentStage',
    'SDGs',
  ];

  const isFileLimitExceeded = useMemo(
    () => selectedFiles.some((selectedFile: SelectedFile) => selectedFile.file.size > MAX_FILE_SIZE),
    [selectedFiles],
  );
  useEffect(() => {
    if (applications.length === 1) {
      // Check if the existing application matches the application definition and isn't submitted already
      const existingApplication = applications[0];
      if (
        existingApplication.applicationDefinitionId === applicationDefinition.id &&
        (existingApplication.status === ApplicationStatus.Draft ||
          existingApplication.status === ApplicationStatus.Apply)
      ) {
        setApplicationId(existingApplication.id);
      }
    } else if (applications.length > 0 && isSavingApplicationEntry === Flag.Success && !applicationId)
      setApplicationId(applications[applications.length - 1].id);
  }, [applications.length, isSavingApplicationEntry]);

  // If there are no application entries, this is the first time the user
  // is accessing the new application page after their first login, so
  // we're saving the application
  useEffect(() => {
    if ((!account || !account?.ownedProjects) && !isFetchingAccount) {
      fetchMyStuff({ bearer });
    }
  }, [applicationDefinition, applications, newProject, defaultApplication, projectId]);
  useEffect(() => {
    if (defaultApplication?.existingProjectId) setProjectId(defaultApplication?.existingProjectId);
  }, [defaultApplication?.existingProjectId]);

  const _getApplication = (): ApplicationEntry => {
    const applicationEntry: ApplicationEntry = {
      id: applicationId,
      applicationDefinitionId: applicationDefinition.id,
      project: applicationDefinition.createProject && !projectId ? newProject : undefined,
      existingProjectId: projectId ?? null,
      applicantId: 0,
      status: defaultApplication?.status || ApplicationStatus.Draft,
      formAnswers: formAnswers.map((answer: FormQuestionAnswer) => ({
        ...answer,
        formId: applicationDefinition.formId,
        answer: answer.answer,
      })),
      projectAnswers: undefined,
      tags: [],
      scores: [],
    };
    const projectForm = applicationDefinition.projectForm;
    if (projectForm) {
      const formattedProjectAnswers = projectAnswers.map((answer: FormQuestionAnswer) => ({
        ...answer,
        formId: projectForm.id,
        answer: answer.answer,
      }));

      applicationEntry.projectAnswers = formattedProjectAnswers;
    }
    return applicationEntry;
  };

  const handleSave = () => {
    if (applicationDefinition.createProject) {
      (newProject || projectId) && saveApplicationEntry(_getApplication());
    } else {
      saveApplicationEntry(_getApplication());
    }
  };

  const onSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();
    const isMissingFormAnswers = isMissingAnswers(applicationDefinition.form?.questions || [], formAnswers);
    const isMissingProjectAnswers = isMissingAnswers(
      applicationDefinition.projectForm?.questions || [],
      projectAnswers,
    );
    const invalidCreateProjectForm =
      applicationDefinition.createProject &&
      projectId === null &&
      (!newProject ||
        requiredFields.some(
          field =>
            (field !== 'SDGs' && field !== 'developmentStage' && !newProject[field]) ||
            (field === 'developmentStage' && community.isDevelopmentStageVisible && !newProject[field]) ||
            (field === 'SDGs' && community.isSDGsVisible && !newProject[field]?.length),
        ));
    // if submit button is clicked and mandatory fields are missing then don't submit the form
    // if save button is clicked then mandatory fields are not required and submitFlag is set to false
    if (
      (isMissingFormAnswers ||
        (projectId === null && isMissingProjectAnswers) ||
        !applicationDefinition ||
        invalidCreateProjectForm) &&
      submitFlag
    ) {
      let missingAnswerRef = null;
      toast(literals.forms_mandatory_fields, { type: 'error' });
      if (invalidCreateProjectForm) {
        const missingField = requiredFields.find(
          field =>
            !newProject ||
            !newProject[field] ||
            (field === 'developmentStage' && community.isDevelopmentStageVisible && !newProject[field]) ||
            (field === 'SDGs' && community.isSDGsVisible && !newProject[field]?.length),
        );
        if (missingField) {
          missingAnswerRef = document.getElementById(`project-${missingField}`);
        }
      } else {
        const projectForm = applicationDefinition.projectForm?.questions || [];
        const form = applicationDefinition.form?.questions || [];

        const missingQuestion =
          getFirstMissingAnswer(projectForm, projectAnswers) || getFirstMissingAnswer(form, formAnswers);
        if (missingQuestion) {
          missingAnswerRef = document.getElementById(`question-${missingQuestion}`);
        }
      }
      if (missingAnswerRef) {
        missingAnswerRef.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      try {
        if (selectedFiles.length) {
          setIsFilesUploading(true);
          const uploadedFiles = await uploadSelectedFiles(selectedFiles, bearer);
          if (!uploadedFiles) {
            toast(literals.upload_resource_too_big_message, { type: 'error' });
            setIsFilesUploading(false);
            return;
          }
          setIsFilesUploading(false);
          clearSelectedFiles();
          if (uploadedFiles) {
            const updatedFormAnswers = formAnswers.map(answer => {
              const matchedAnswerIndex = selectedFiles.findIndex(
                (file: SelectedFile) => file.question.id === answer.formQuestionId,
              );
              if (matchedAnswerIndex > -1) return { ...answer, answer: uploadedFiles[matchedAnswerIndex] };
              return answer;
            });
            setFormAnswers(updatedFormAnswers);
            const updatedProjectAnswers = projectAnswers.map(answer => {
              const matchedAnswerIndex = selectedFiles.findIndex(
                (file: SelectedFile) => file.question.id === answer.formQuestionId,
              );
              if (matchedAnswerIndex > -1) return { ...answer, answer: uploadedFiles[matchedAnswerIndex] };
              return answer;
            });
            setProjectFormAnswers(updatedProjectAnswers);
            setIsFormSubmitted(true);
          }
        } else {
          setIsFormSubmitted(true);
        }
      } catch (error) {
        toast(literals.global_something_went_wrong, { type: 'error' });
      }
    }
  };

  useEffect(() => {
    if (isFormSubmitted) {
      if (submitFlag) {
        setSubmitFlag(false);
        handleSubmitApplicationEntry();
        setIsFormSubmitting(true);
      } else {
        handleSave();
        if (applicationDefinition?.trackApplicationSave) {
          setTrackingScripts([...trackingScripts, applicationDefinition.trackApplicationSave]);
        }
        setIsFormSaving(true);
      }
      setIsFormSubmitted(false);
    }
  }, [isFormSubmitted]);

  const handleExit = () => {
    history.push('/account/mystuff');
  };

  const handleSubmitApplicationEntry = () => {
    submitApplication && submitApplication({ ..._getApplication(), status: ApplicationStatus.Pending });

    if (applicationDefinition?.trackApplicationSubmission) {
      setTrackingScripts([...trackingScripts, applicationDefinition.trackApplicationSubmission]);
    }
  };

  const isSubmitPeriod = moment(applicationDefinition?.endDate).isAfter(moment());

  return (
    <div style={{ display: 'flex', width: '100%', flexDirection: 'column', gap: '4em' }}>
      <CoverComponent
        style={{ padding: '0' }}
        communityId={applicationDefinition.communityId}
        applicationDefinitionId={applicationDefinition.id}
      />
      <StyledLogo
        style={{ position: 'absolute', top: '12.5em', left: '2em' }}
        src={applicationDefinition.communityLogo || ''}
      />
      <Container onSubmit={onSubmit} onKeyPress={e => e.key === 'Enter' && e.preventDefault()}>
        <ApplicationContainer>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem', marginBottom: '2rem' }}>
            <Row>
              <InnerHtmlWrapper
                style={{ textAlign: 'left' }}
                html={applicationDefinition?.form?.description || applicationDefinition?.description || ''}
              />
            </Row>
            {applicationDefinition.createProject && (
              <ProjectForm
                community={community}
                showSteps={[1, 3, 4, 5]}
                formMode={true}
                communityId={applicationDefinition.communityId}
                updateFormMode={setNewProject}
                defaultProject={defaultApplication?.project || undefined}
                showMandatoryQuestions={true}
                isFormSubmitting={submitFlag}
                defaultProjectId={defaultApplication?.existingProjectId || null}
                setProjectId={setProjectId}
              />
            )}
            {applicationDefinition.projectForm &&
              !projectId &&
              applicationDefinition.projectForm?.questions?.length > 0 && (
                <NewForm
                  form={applicationDefinition?.projectForm || []}
                  hideDetails
                  defaultAnswers={defaultApplication?.projectAnswers || []}
                  setAnswers={answers =>
                    setProjectFormAnswers(
                      answers.map(answer => ({ ...answer, formId: applicationDefinition?.projectForm?.id || 0 })),
                    )
                  }
                  isFilesUploading={isFilesUploading}
                  isFormSubmitting={submitFlag}
                />
              )}
            {applicationDefinition.form && applicationDefinition.form?.questions?.length > 0 && (
              <NewForm
                defaultAnswers={defaultApplication?.formAnswers || []}
                form={applicationDefinition.form}
                setAnswers={setFormAnswers}
                hideDetails={true}
                isFilesUploading={isFilesUploading}
                isFormSubmitting={submitFlag}
              />
            )}
          </div>
        </ApplicationContainer>
        <Sidebar>
          <div>
            <Row style={{ fontWeight: 600, lineHeight: '1.3em' }}>
              {overrideTranslation('application_deadline_label') || literals.application_deadline_label}
            </Row>
            <Row style={{ lineHeight: '1.3em', marginBottom: '1em' }}>
              {moment(applicationDefinition?.endDate).format('DD MMMM YYYY')}
            </Row>
          </div>
          <Row style={{ width: '100%' }}>
            {defaultApplication?.status !== ApplicationStatus.Pending && (
              <AcceptButton
                loading={isSavingApplicationEntry === Flag.Request && isFormSaving}
                size="big"
                disabled={
                  isSavingApplicationEntry === Flag.Request ||
                  isFormSubmitted ||
                  isFilesUploading ||
                  isFileLimitExceeded
                }
                type="submit"
                style={{ flex: '1' }}
                onClick={() => !isFileLimitExceeded && setSubmitFlag(false)}
              >
                {literals.community_edit_save_button}
              </AcceptButton>
            )}
            <DenyButton size="big" type="button" onClick={handleExit} style={{ flex: '1' }}>
              {literals.global_exit}
            </DenyButton>
          </Row>
          {submitApplication && (
            <Row style={{ width: '100%' }}>
              {((defaultApplication &&
                (defaultApplication?.status === ApplicationStatus.Apply ||
                  defaultApplication?.status === ApplicationStatus.Draft)) ||
                !defaultApplication) &&
                isSubmitPeriod && (
                  <SubmitButton
                    disabled={
                      isSavingApplicationEntry === Flag.Request ||
                      isFormSubmitting ||
                      isFilesUploading ||
                      isFileLimitExceeded
                    }
                    size="big"
                    loading={isSavingApplicationEntry === Flag.Request && isFormSubmitting}
                    onClick={() => !isFileLimitExceeded && setSubmitFlag(true)}
                    type="submit"
                  >
                    {literals.global_submit}
                  </SubmitButton>
                )}

              {!isSubmitPeriod && <span>{literals.application_closed_period}</span>}
            </Row>
          )}
          {defaultApplication && (
            <Row>
              <PrimaryLabel
                style={{
                  flex: '1',
                }}
              >
                {getStatusLiteral(
                  defaultApplication.status === ApplicationStatus.Apply
                    ? ApplicationStatus.Draft
                    : defaultApplication.status,
                  literals,
                )}
              </PrimaryLabel>
            </Row>
          )}
        </Sidebar>
      </Container>
    </div>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(NewProjectApplication);
